/**
 * Decision Engine - Limits & Excesses Management
 *
 * Manages limit and excess definitions for the decision engine.
 * v2 SafetyCulture format with insuring clauses and entry types.
 */

import { DrawerFooter } from '@client/components/_core/layout/DrawerFooter';
import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { SectionHeader } from '@client/components/_ui/data-display/SectionHeader';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useFormDirty } from '@client/hooks/forms/useFormDirty';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { Badge, Form, Input, InputNumber, Select, Switch, Tag } from 'antd';
import { Gauge, Plus } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

type LimitExcessFormInput = {
    insuringClause: string;
    entryType: string;
    name: string;
    appliesWhen: string | null;
    type: string | null;
    relationship: string | null;
    wordingReference: string | null;
    scheduleLocation: string | null;
    deInstruction: string | null;
    sortOrder: number;
    isActive: boolean;
};

const FormItem = Form.Item<LimitExcessFormInput>;

const ENTRY_TYPE_OPTIONS = [
    { label: 'Limit', value: 'LIMIT' },
    { label: 'Declared value', value: 'DECLARED_VALUE' },
    { label: 'Additional benefit limit', value: 'ADDITIONAL_BENEFIT_LIMIT' },
    { label: 'Excess', value: 'EXCESS' },
];

const ENTRY_TYPE_COLOURS: Record<string, string> = {
    LIMIT: 'blue',
    DECLARED_VALUE: 'green',
    ADDITIONAL_BENEFIT_LIMIT: 'purple',
    EXCESS: 'orange',
};

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/product-types/$productTypeId/decision-engine/limits-excesses',
)({
    staticData: {
        metadata: {
            title: 'Limits and excesses',
        },
    },
    component: LimitsExcessesManagement,
});

function LimitsExcessesManagement() {
    const { productTypeId } = useParams({ strict: false });
    const { trpc, trpcUtils } = useTrpc();
    const [form] = Form.useForm<LimitExcessFormInput>();

    // Drawer state
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingId, setEditingId] = useState<bigint | null>(null);
    // The form mounts with the drawer, so the values it opens on are held here and passed as `initialValues`.
    const initialValuesRef = useRef<LimitExcessFormInput | null>(null);
    const formDirty = useFormDirty();
    const closeRef = useRef<(() => void) | null>(null);
    const formId = 'limit-excess-form';

    // Fetch limits and excesses
    const { data, isLoading } = trpc.claim.decisionEngine.admin.listLimitsAndExcesses.useQuery(
        { productTypeId: BigInt(productTypeId!) },
        { enabled: !!productTypeId },
    );
    const limitsAndExcesses = data?.rows ?? [];

    // Mutations
    const { mutate: createLimitExcess, isPending: isCreating } =
        trpc.claim.decisionEngine.admin.createLimitExcess.useMutation({
            onSuccess() {
                message.success('Limit/excess created.');
                void trpcUtils.claim.decisionEngine.admin.listLimitsAndExcesses.invalidate();
                void trpcUtils.claim.decisionEngine.getConfig.invalidate({ productTypeId: BigInt(productTypeId!) });
                closeRef.current?.();
            },
            onError(error) {
                errorMessage.show(error);
            },
        });

    const { mutate: updateLimitExcess, isPending: isUpdating } =
        trpc.claim.decisionEngine.admin.updateLimitExcess.useMutation({
            onSuccess() {
                message.success('Limit/excess updated.');
                void trpcUtils.claim.decisionEngine.admin.listLimitsAndExcesses.invalidate();
                void trpcUtils.claim.decisionEngine.getConfig.invalidate({ productTypeId: BigInt(productTypeId!) });
                closeRef.current?.();
            },
            onError(error) {
                errorMessage.show(error);
            },
        });

    const { mutate: deleteLimitExcess } = trpc.claim.decisionEngine.admin.deleteLimitExcess.useMutation({
        onSuccess() {
            message.success('Limit/excess deleted.');
            void trpcUtils.claim.decisionEngine.admin.listLimitsAndExcesses.invalidate();
            void trpcUtils.claim.decisionEngine.getConfig.invalidate({ productTypeId: BigInt(productTypeId!) });
        },
        onError(error) {
            errorMessage.show(error);
        },
    });

    // Table columns
    const columnHelper = createColumnHelper<(typeof limitsAndExcesses)[number]>();

    const columns = useMemo(
        () => [
            columnHelper.accessor('insuringClause', {
                id: 'insuringClause',
                header: 'Insuring clause',
                width: 200,
            }),
            columnHelper.accessor('entryType', {
                id: 'entryType',
                header: 'Entry type',
                width: 180,
                cell: ({ row }) => <Tag color={ENTRY_TYPE_COLOURS[row.entryType] ?? 'default'}>{row.entryType}</Tag>,
            }),
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Name',
                width: 220,
            }),
            columnHelper.accessor('appliesWhen', {
                id: 'appliesWhen',
                header: 'Applies when',
                width: 200,
                cell: ({ row }) => {
                    if (!row.appliesWhen) return <span className="text-muted-foreground">—</span>;
                    const truncated =
                        row.appliesWhen.length > 60 ? `${row.appliesWhen.slice(0, 60)}...` : row.appliesWhen;
                    return <span title={row.appliesWhen}>{truncated}</span>;
                },
            }),
            columnHelper.accessor('type', {
                id: 'type',
                header: 'Type',
                width: 120,
                cell: ({ row }) => row.type ?? <span className="text-muted-foreground">—</span>,
            }),
            columnHelper.accessor('isActive', {
                id: 'isActive',
                header: 'Status',
                width: 100,
                cell: ({ row }) => (
                    <Badge status={row.isActive ? 'success' : 'default'} text={row.isActive ? 'Active' : 'Inactive'} />
                ),
            }),
            columnHelper.actions({
                id: 'actions',
                cell: ({ row }) => (
                    <Space size="small">
                        <Button.Edit onClick={() => openEditDrawer(row)} />
                        <Button.Delete
                            confirmTitle="Delete limit/excess"
                            confirmDescription="Are you sure you want to delete this limit/excess definition?"
                            onConfirm={(onSuccess) =>
                                deleteLimitExcess(
                                    { id: row.id },
                                    {
                                        onSuccess: () => {
                                            onSuccess();
                                        },
                                        onError: (error) => {
                                            errorMessage.show(error);
                                        },
                                    },
                                )
                            }
                        />
                    </Space>
                ),
                fixed: 'right',
            }),
        ],
        [columnHelper, deleteLimitExcess],
    );

    const tableProps = usePrismaTable({
        data: limitsAndExcesses,
        total: data?.total ?? 0,
        loading: isLoading,
        columns,
        rowKey: 'id',
        pagination: { position: 'none' },
        emptyState: { noRecordsTitle: 'No limits or excesses yet' },
    });

    // Drawer handlers
    const openCreateDrawer = () => {
        setEditingId(null);
        initialValuesRef.current = {
            insuringClause: '',
            entryType: 'LIMIT',
            name: '',
            appliesWhen: null,
            type: null,
            relationship: null,
            wordingReference: null,
            scheduleLocation: null,
            deInstruction: null,
            sortOrder: 0,
            isActive: true,
        };
        setDrawerOpen(true);
    };

    const openEditDrawer = (item: (typeof limitsAndExcesses)[number]) => {
        setEditingId(item.id);
        initialValuesRef.current = {
            insuringClause: item.insuringClause,
            entryType: item.entryType,
            name: item.name,
            appliesWhen: item.appliesWhen,
            type: item.type,
            relationship: item.relationship,
            wordingReference: item.wordingReference,
            scheduleLocation: item.scheduleLocation,
            deInstruction: item.deInstruction,
            sortOrder: item.sortOrder,
            isActive: item.isActive,
        };
        setDrawerOpen(true);
    };

    // Runs after the closing animation, whether the drawer was dismissed or closed after a save.
    const resetDrawer = () => {
        form.resetFields();
        formDirty.markClean();
        initialValuesRef.current = null;
        setDrawerOpen(false);
        setEditingId(null);
    };

    const onFinish = (values: LimitExcessFormInput) => {
        const data = {
            insuringClause: values.insuringClause,
            entryType: values.entryType,
            name: values.name,
            appliesWhen: values.appliesWhen,
            type: values.type,
            relationship: values.relationship,
            wordingReference: values.wordingReference,
            scheduleLocation: values.scheduleLocation,
            deInstruction: values.deInstruction,
            sortOrder: values.sortOrder,
            isActive: values.isActive,
        };

        if (editingId) {
            updateLimitExcess({ id: editingId, data });
        } else {
            createLimitExcess({ productTypeId: BigInt(productTypeId!), data });
        }
    };

    return (
        <PageController route={Route}>
            <div className="mb-6 flex items-start gap-4 rounded-lg border border-violet-200 bg-violet-50 p-4 dark:border-violet-800 dark:bg-violet-950/30">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-violet-100 dark:bg-violet-900/50">
                    <Gauge className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                </div>
                <div>
                    <SectionHeader as="h3" size="small" title="Limits and excesses" className="mb-1" />
                    <p className="text-muted-foreground text-sm">
                        Define limits and excess entries within coverage policies. Each entry is linked to an insuring
                        clause with specific entry types (limit, declared value, additional benefit limit, or excess).
                    </p>
                </div>
            </div>
            <TableToolbar
                actions={
                    <Button type="primary" icon={<Plus />} onClick={openCreateDrawer}>
                        Add limit/excess
                    </Button>
                }
            />
            <PrismaTable {...tableProps} />

            {drawerOpen && (
                <OverlayPage
                    title={editingId ? 'Edit limit/excess' : 'Add limit/excess'}
                    size="small"
                    isDirty={formDirty.isDirty}
                    afterClose={resetDrawer}
                    footer={({ requestClose }) => (
                        <DrawerFooter
                            onCancel={requestClose}
                            formId={formId}
                            okText={editingId ? 'Save changes' : 'Create'}
                            okLoading={isCreating || isUpdating}
                        />
                    )}
                >
                    {({ onClose }) => {
                        closeRef.current = onClose;
                        return (
                            <Form<LimitExcessFormInput>
                                form={form}
                                {...formDirty.formProps}
                                layout="vertical"
                                onFinish={onFinish}
                                id={formId}
                                clearOnDestroy
                                initialValues={initialValuesRef.current ?? undefined}
                            >
                                <FormItem
                                    label="Insuring clause"
                                    name="insuringClause"
                                    rules={[{ required: true, message: 'Insuring clause is required' }]}
                                >
                                    <Input placeholder="e.g., Professional indemnity" />
                                </FormItem>

                                <FormItem
                                    label="Entry type"
                                    name="entryType"
                                    rules={[{ required: true, message: 'Entry type is required' }]}
                                >
                                    <Select placeholder="Select entry type" options={ENTRY_TYPE_OPTIONS} />
                                </FormItem>

                                <FormItem
                                    label="Name"
                                    name="name"
                                    rules={[{ required: true, message: 'Name is required' }]}
                                >
                                    <Input placeholder="e.g., Criminal defence costs" />
                                </FormItem>

                                <FormItem label="Applies when" name="appliesWhen">
                                    <Input.TextArea rows={3} placeholder="Conditions under which this entry applies" />
                                </FormItem>

                                <FormItem label="Type" name="type">
                                    <Input placeholder="e.g., Sub-limit, Aggregate" />
                                </FormItem>

                                <FormItem label="Relationship" name="relationship">
                                    <Input.TextArea rows={3} placeholder="Relationship to other limits or coverage" />
                                </FormItem>

                                <FormItem label="Wording reference" name="wordingReference">
                                    <Input placeholder="e.g., Section 4.2" />
                                </FormItem>

                                <FormItem label="Schedule location" name="scheduleLocation">
                                    <Input placeholder="e.g., Schedule 1, Item 5" />
                                </FormItem>

                                <FormItem
                                    label="DE instruction"
                                    name="deInstruction"
                                    tooltip="Instructions for the decision engine when processing this entry"
                                >
                                    <Input.TextArea
                                        rows={3}
                                        placeholder="Processing instructions for the decision engine"
                                    />
                                </FormItem>

                                <div className="mt-4 border-separator border-t pt-4">
                                    <FormItem label="Sort order" name="sortOrder" tooltip="Lower numbers appear first">
                                        <InputNumber min={0} className="w-full" />
                                    </FormItem>

                                    <FormItem label="Active" name="isActive" valuePropName="checked">
                                        <Switch />
                                    </FormItem>
                                </div>
                            </Form>
                        );
                    }}
                </OverlayPage>
            )}
        </PageController>
    );
}
